import { Col, Form, Input, Row } from "antd"
import { getLocalStorageBrowserLocation } from "helpers/location"
import { debounce } from "lodash"
import GooglePlacesNewInput from "modules/google/GooglePlacesNewInput"
import useGeocoder from "modules/google/hooks/useGeocoder"
import usePredictions from "modules/google/hooks/usePredictions"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { tripLatLngSelector } from "store/trips"
import { LocationCard } from "pagesV2/pro/Experiences/ListingLocation/LocationCard"
import { LocationInfo } from "pagesV2/pro/Experiences/ListingLocation/LocationForm"
import TripLocation from "pagesV2/pro/Experiences/ListingLocation/utils/Location"
import { buildLocationPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"
import style from "pagesV2/pro/Experiences/ListingLocation/ListingLocation.module.css"

const { TextArea } = Input

/**
 * Location step — Google Places search, map pin, additional directions.
 *
 * meetingLatLng (address string) and centerLatLng ({lat, lng}) are local state.
 * The form field "address" is kept in sync with meetingLatLng so it's available
 * in the shared form store and survives remounts.
 */
export const LocationStep = () => {
  const { t } = useTranslation()
  const { form, stepSaveHandlersRef, isFormInitialized } = useWizardContext()

  const tripLatLng = useSelector(tripLatLngSelector)
  const geocoder = useGeocoder(null)
  const browserLocation = useMemo(() => getLocalStorageBrowserLocation(), [])

  const [centerLatLng, setCenterLatLng] = useState(tripLatLng || browserLocation)
  const [meetingLatLng, setMeetingLatLng] = useState(() => form.getFieldValue("address") || null)
  const [manualAddress, setManualAddress] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectPlace] = usePredictions()

  // Sync tripLatLng when it loads
  useEffect(() => {
    if (tripLatLng) setCenterLatLng(tripLatLng)
  }, [tripLatLng])

  // Seed meetingLatLng from the form once the wizard has populated it from the
  // trip. The lazy useState initializer above runs at mount — if the trip hasn't
  // loaded yet at that point the address field is empty and meetingLatLng stays
  // null. This effect corrects that on the first initialization tick.
  useEffect(() => {
    if (!isFormInitialized) return
    const savedAddress = form.getFieldValue("address")
    if (savedAddress && !meetingLatLng) {
      setMeetingLatLng(savedAddress)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormInitialized])

  useEffect(() => {
    if (!meetingLatLng) {
      form.setFieldsValue({ address: "" })
    } else {
      form.setFieldsValue({ address: meetingLatLng })
    }
  }, [meetingLatLng, form])

  const getAddressComponent = useCallback((addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }, [])

  const parseAddressComponents = useCallback(
    (place) => {
      const addressComponents = place.address_components
      const getComponent = (types) => getAddressComponent(addressComponents, types)
      const streetNumber = getComponent(["street_number"])
      const route = getComponent(["route"])
      const premise = getComponent(["premise"])
      const subpremise = getComponent(["subpremise"])

      let streetAddress = ""
      if (streetNumber || route) {
        streetAddress = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ")
      }
      if (!streetAddress && place.formatted_address) {
        streetAddress = place.formatted_address.split(",")[0]?.trim() || ""
      }
      if (subpremise?.short_name) {
        streetAddress = [subpremise.short_name, streetAddress].filter(Boolean).join(", ")
      } else if (premise?.short_name && !streetAddress.includes(premise.short_name)) {
        streetAddress = [premise.short_name, streetAddress].filter(Boolean).join(", ")
      }

      const city =
        getComponent(["locality"]) ||
        getComponent(["postal_town"]) ||
        getComponent(["sublocality"]) ||
        getComponent(["administrative_area_level_2"]) ||
        getComponent(["administrative_area_level_3"])

      const state = getComponent(["administrative_area_level_1"])
      const country = getComponent(["country"])
      const zip = getComponent(["postal_code"])

      return {
        street_address: streetAddress,
        city: city?.long_name || "",
        state: state?.short_name || state?.long_name || "",
        country: country?.long_name || "",
        zip_code: zip?.long_name || ""
      }
    },
    [getAddressComponent]
  )

  const updateManualAddressFields = useCallback(
    (place) => {
      if (manualAddress && place.address_components) {
        form.setFieldsValue(parseAddressComponents(place))
      }
    },
    [manualAddress, form, parseAddressComponents]
  )

  const handleGeocoderResult = useCallback(
    (place) => {
      if (place?.formatted_address) {
        setMeetingLatLng(place.formatted_address)
        form.setFieldsValue({ address: place.formatted_address })
        updateManualAddressFields(place)
      }
    },
    [form, updateManualAddressFields]
  )

  // eslint-disable-next-line
  const debounceCoordinatesToAddress = useCallback(
    debounce(
      (location) =>
        geocoder({ location }, (place) => {
          const address = place.formatted_address
          form.setFieldsValue({ address })
        }),
      200
    ),
    []
  )

  const placeChangedHandler = useCallback(({ target: { coordinates, value } }) => {
    setIsEditing(false)
    if (coordinates) debounceCoordinatesToAddress(coordinates)
    setCenterLatLng(coordinates)
    if (value) setMeetingLatLng(value)
  }, []) // eslint-disable-line

  const handleMarkerDragEnd = useCallback(
    (newCoordinates) => {
      setCenterLatLng(newCoordinates)
      geocoder({ location: newCoordinates }, handleGeocoderResult)
    },
    [geocoder, handleGeocoderResult]
  )

  // Register save handler — address validation is handled by stepConfig fields (validateStep)
  useEffect(() => {
    stepSaveHandlersRef.current["location"] = async () => {
      // No meeting point yet (a half-filled draft): only the directions are saved — the map's centre
      // is just where the browser is.
      if (!meetingLatLng) return buildLocationPayload({ additional_directions: form.getFieldValue("additional_directions") })
      let resolvedCoordinates = centerLatLng
      if (!resolvedCoordinates?.lat || !resolvedCoordinates?.lng) {
        const geocoded = await new Promise((resolve) => {
          try {
            geocoder({ address: meetingLatLng }, (place) => {
              const location = place?.geometry?.location
              resolve(location ? { lat: location.lat(), lng: location.lng() } : null)
            })
          } catch {
            resolve(null)
          }
        })

        if (!geocoded?.lat || !geocoded?.lng) {
          form.setFields([{ name: "address", errors: [t("experiences.validation.location_coordinates_error")] }])
          throw new Error("No coordinates resolved")
        }
        resolvedCoordinates = geocoded
        setCenterLatLng(resolvedCoordinates)
      }

      const formValues = form.getFieldsValue()
      return buildLocationPayload({
        additional_directions: formValues.additional_directions,
        meetingLatLng,
        coordinates: resolvedCoordinates
      })
    }
    return () => {
      delete stepSaveHandlersRef.current["location"]
    }
  }, [form, stepSaveHandlersRef, meetingLatLng, centerLatLng, geocoder])

  return (
    <div>
      <Row gutter={[16, 16]} className="mb-3xl">
        <Col xs={24} sm={24} md={12}>
          {(meetingLatLng && !isEditing) ||
            (!manualAddress && (
              <Form.Item name="address" rules={[{ required: true, message: t("experiences.validation.address_required") }]}>
                <GooglePlacesNewInput
                  placeholder="Enter your address"
                  bsSize="sm"
                  onChange={placeChangedHandler}
                  dropDownMenuClassName="mt-2 shadow-sm"
                  resetIfChanged
                  meetingLatLng={meetingLatLng}
                  manualAddress={manualAddress}
                  setManualAddress={setManualAddress}
                  setCenterLatLng={setCenterLatLng}
                  setMeetingLatLng={setMeetingLatLng}
                />
              </Form.Item>
            ))}
          {meetingLatLng && !isEditing && !manualAddress && (
            <LocationCard name="location" meetingLatLng={meetingLatLng} onEdit={() => setIsEditing(true)} t={t} />
          )}
          {manualAddress && (
            <LocationInfo
              form={form}
              t={t}
              setManualAddress={setManualAddress}
              setCenterLatLng={setCenterLatLng}
              setMeetingLatLng={setMeetingLatLng}
              selectPlace={selectPlace}
              showMapVerificationMessage
            />
          )}
          {((meetingLatLng && !isEditing) || manualAddress) && (
            <div className={`mt-20 ${style.addresField}`}>
              <Form.Item name="additional_directions" label={t("listings.tabs.location.special_instructions")}>
                <TextArea placeholder={t("listings.tabs.location.special_instructions_placeholder")} rows={4} />
              </Form.Item>
            </div>
          )}
        </Col>
        <Col xs={24} sm={24} md={12}>
          <TripLocation height={450} centerLatLng={centerLatLng} meetingLatLng={meetingLatLng} onMarkerDragEnd={handleMarkerDragEnd} />
          {((meetingLatLng && !isEditing) || manualAddress) && (
            <div className={`mt-20 ${style.addresFieldMobile}`}>
              <Form.Item name="additional_directions" label={t("listings.tabs.location.special_instructions")}>
                <TextArea placeholder={t("listings.tabs.location.special_instructions_placeholder")} rows={4} />
              </Form.Item>
            </div>
          )}
        </Col>
      </Row>
    </div>
  )
}
